import { markImageUrlForExport, getDefaultWatermarkEnabled } from "./aiProvenance";

/**
 * Trigger a browser download for a remote URL by fetching the file as a
 * blob and using an object URL. This avoids cross-origin navigation, which
 * sandboxed environments (e.g. Claude Preview) block when the URL points
 * outside localhost. Falls back to a direct link if fetching fails.
 */
export interface DownloadOptions {
  /**
   * Burn the visible "AI generated" label into the exported image.
   * Defaults to the active workspace's setting. The machine-readable XMP
   * provenance mark is applied regardless — it is not optional.
   */
  watermark?: boolean;
  /**
   * Skip AI-provenance marking entirely. Only for non-image downloads
   * (e.g. video files), where the image pipeline doesn't apply.
   */
  skipAiMarking?: boolean;
}

export async function downloadUrlAsFile(
  url: string,
  filename: string,
  options: DownloadOptions = {},
): Promise<void> {
  if (!url) return;

  // Every image export goes through provenance marking here rather than at
  // each call site — this is the single choke point, so a new download path
  // can't accidentally ship an unmarked asset (EU AI Act, Article 50).
  if (!options.skipAiMarking && isLikelyImage(filename)) {
    let markedUrl: string | null = null;
    try {
      markedUrl = await markImageUrlForExport(
        url,
        options.watermark ?? getDefaultWatermarkEnabled(),
      );
      // Marking re-encodes through a canvas and always emits PNG bytes, so the
      // extension has to follow — otherwise a .jpg name would carry PNG data.
      triggerDownload(markedUrl, toPngFilename(filename));
      setTimeout(() => URL.revokeObjectURL(markedUrl!), 10_000);
      return;
    } catch (err) {
      // Marking is best-effort — never block the user's download.
      console.warn("AI provenance marking failed, downloading unmarked:", err);
      if (markedUrl) URL.revokeObjectURL(markedUrl);
    }
  }

  // Data URLs can be downloaded directly — no fetch needed.
  if (url.startsWith("data:") || url.startsWith("blob:")) {
    triggerDownload(url, filename);
    return;
  }

  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    triggerDownload(objectUrl, filename);
    // Revoke after the click has had a chance to start the download.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  } catch (err) {
    console.warn("Blob download failed, falling back to direct link:", err);
    triggerDownload(url, filename);
  }
}

/** Marking only applies to raster images; videos and other assets pass through. */
function isLikelyImage(filename: string): boolean {
  return /\.(png|jpe?g|webp)$/i.test(filename);
}

/** Swap any raster extension for .png, matching the bytes we actually emit. */
function toPngFilename(filename: string): string {
  return filename.replace(/\.(jpe?g|webp)$/i, ".png");
}

function triggerDownload(href: string, filename: string) {
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
